import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from '../BasicElement';
import Image, { IImage } from '../Image';
import { ILink } from '../Button';
import VHSBreadcrumb from '../VHSBreadcrumb';
import { useHashAsState } from '../../hooks';
export interface IVHSHeroChecklistItems {
	/**
	 * Checklist Label
	 * @control richtext
	 */
	label?: string;
	/**
	 *  Checklist Sublabel
	 */
	sublabel?: string;
}
export interface IVHSHeroAuthorItems {
	/**
	 * Author Link
	 */
	link?: ILink;
	/**
	 *  Author Name
	 */
	name?: string;
	/**
	 *  Author Title
	 */
	title?: string;
	/**
	 * Author Image
	 */
	image?: IImage;
}

export interface IVHSHeroCircleItems {
	/**
	 * Circle Image
	 */
	image?: IImage;
	/**
	 *  Circle Label
	 */
	label?: string;
}

export interface IVHSHeroProps extends IBaseElementProps {
	/**
	 * Style
	 */
	style:
		| 'primary'
		| 'secondary'
		| 'main'
		| 'index'
		| 'search'
		| 'course'
		| 'single'
		| 'split'
		| 'split-mini'
		| 'centre'
		| 'profile'
		| 'blog';
	/**
	 * Breadcrumbs
	 */
	breadcrumbs?: ILink[];
	/**
	 * Initials
	 */
	initials?: string;
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Subheading
	 */
	subheading?: string;
	/**
	 * Checklist
	 */
	checklist?: IVHSHeroChecklistItems[];
	/**
	 * Date
	 */
	date?: string;
	/**
	 * Text
	 */
	text?: string;
	/**
	 * Image
	 */
	image?: IImage;
	/**
	 * Circles
	 */
	circles?: IVHSHeroCircleItems[];
	/**
	 * Author
	 */
	authors?: IVHSHeroAuthorItems[];
	/**
	 * Button
	 */
	button?: ILink;
	/**
	 * Tags
	 */
	tags?: ILink[];
	/**
	 * @noUI
	 */
	accountRegisteredText?: string;
}

/**
 * Hero
 * @block
 * @icon superhero
 */
const VHSHero: React.FC<IVHSHeroProps> = ({
	style,
	breadcrumbs,
	initials,
	heading,
	subheading,
	date,
	checklist = [],
	text,
	image,
	circles = [],
	authors = [],
	button,
	tags,
	accountRegisteredText,
}) => {
	const [state, hash, updateState, normalizeState] = useHashAsState({
		parameters: {
			total: 'int',
			search: 'string',
		},
		defaults: {
			search: '',
		},
	});
	return (
		<div
			className={cn(
				'vhs-hero',
				(style === 'primary' || !style) && 'vhs-hero--primary',
				style === 'secondary' && 'vhs-hero--secondary',
				style === 'main' && 'vhs-hero--main',
				style === 'index' && 'vhs-hero--index',
				style === 'search' && 'vhs-hero--search',
				style === 'blog' && 'vhs-hero--search',
				style === 'course' && 'vhs-hero--course',
				style === 'split' && 'vhs-hero--split',
				style === 'split-mini' && 'vhs-hero--split-mini',
				style === 'centre' && 'vhs-hero--centre',
				style === 'profile' && 'vhs-hero--profile',
				style === 'single' && 'vhs-hero--single',
			)}
		>
			<div className="vhs-hero__container">
				<div className="vhs-hero__wrapper">
					{style === 'index' && (
						<div className="vhs-hero__big-apple"></div>
					)}
					<div
						className={cn(
							'vhs-hero__content',
							button?.label && 'vhs-hero__content--button',
						)}
					>
						{style === 'profile' && initials && (
							<div className="vhs-hero__initials">{initials}</div>
						)}
						{style != 'main' &&
							style !== 'profile' &&
							breadcrumbs?.length > 0 && (
								<VHSBreadcrumb
									// @ts-ignore
									breadcrumbs={breadcrumbs}
								/>
							)}
						{style === 'index' && accountRegisteredText && (
							<div className="vhs-hero__disclaimer">
								{accountRegisteredText}
							</div>
						)}
						{heading && (
							<h1
								className="vhs-hero__heading"
								dangerouslySetInnerHTML={{ __html: heading }}
							/>
						)}
						{(style === 'search' || style === 'blog') && (
							<>
								{state?.search && (
									<h1 className="vhs-hero__heading">
										{state.total ? state.total : 'No'}{' '}
										result{state.total != 1 ? 's' : ''} for
										"{state.search}"
									</h1>
								)}
								{!state?.search && (
									<h1 className="vhs-hero__heading">
										{style === 'search'
											? 'Search Results'
											: 'Blogs'}
									</h1>
								)}
							</>
						)}
						{subheading && (
							<h2 className="vhs-hero__subheading">
								{subheading}
							</h2>
						)}
						{style === 'single' && date && (
							<h2 className="vhs-hero__date">{date}</h2>
						)}
						{tags && tags.length > 0 && (
							<ul className="vhs-hero__tags">
								{tags.map(({ label, href }, index) => (
									<li className="vhs-hero__tag" key={index}>
										<a
											href={href}
											dangerouslySetInnerHTML={{
												__html: label,
											}}
										/>
									</li>
								))}
							</ul>
						)}
						{checklist.length > 0 && (
							<ul className="vhs-hero__checklist">
								{checklist.map(({ label, sublabel }, index) => (
									<li
										className={cn(
											'vhs-hero__checklist-item',
											sublabel &&
												'vhs-hero__checklist-item--sublabel',
										)}
										key={index}
									>
										<i className="icon icon-checkmark"></i>
										<div className="vhs-hero__checklist-label">
											<div
												dangerouslySetInnerHTML={{
													__html: label,
												}}
											/>
											{sublabel && (
												<div className="vhs-hero__checklist-sublabel">
													{sublabel}
												</div>
											)}
										</div>
									</li>
								))}
							</ul>
						)}
						{style === 'course' &&
							authors.length > 0 &&
							(authors || []).map((author, index) => (
								<div key={index} className="vhs-hero__author">
									{author?.image?.src && (
										<div className="vhs-hero__author-image-wrapper">
											<Image
												{...author.image}
												className="vhs-hero__author-image img-fluid"
											/>
										</div>
									)}
									<div>
										<div className="vhs-hero__title">
											{author.title}
										</div>
										<div className="vhs-hero__name">
											{author.name}
											{author?.link?.href && (
												<>
													<span>, </span>
													<a
														href={
															author?.link?.href
														}
														target={
															author?.link?.target
														}
														rel={
															author?.link
																?.target ===
															'_blank'
																? 'noopener noreferrer'
																: ''
														}
													>
														{author?.link?.label}
													</a>
												</>
											)}
										</div>
									</div>
								</div>
							))}
						{style === 'main' &&
							circles &&
							circles.length > 0 &&
							circles.map(({ image, label }, index) => {
								const content = (
									<div
										className={cn(
											'vhs-hero__circle-wrapper',
											`vhs-hero__circle-wrapper--${index}`,
										)}
										key={index}
									>
										{image.src && (
											<div
												className={cn(
													'vhs-hero__circle-image-wrapper',
													label &&
														'vhs-hero__circle-image-wrapper--label',
												)}
											>
												<Image
													{...image}
													className="vhs-hero__circle-image img-fluid"
												/>
											</div>
										)}
										{label && (
											<div className="vhs-hero__circle-label">
												{label}
											</div>
										)}
									</div>
								);
								if (index === 0) {
									return (
										<div
											style={{ position: 'relative' }}
											key={index}
										>
											{content}
										</div>
									);
								} else {
									return content;
								}
							})}
						{style === 'split' && image?.src && (
							<div className="vhs-hero__small-apple"></div>
						)}
						{style === 'split-mini' && image?.src && (
							<div className="vhs-hero__small-apple"></div>
						)}
						{(style === 'main' ||
							style === 'split' ||
							style === 'split-mini' ||
							style === 'centre') &&
							image?.src && (
								<div className="vhs-hero__image-wrapper">
									<Image
										{...image}
										className="vhs-hero__image img-fluid"
									/>
								</div>
							)}
						{(style === 'centre' || style === 'index') && text && (
							<p
								className="vhs-hero__text"
								dangerouslySetInnerHTML={{ __html: text }}
							/>
						)}
						{button?.label && (
							<a
								href={button?.href}
								target={button?.target}
								rel={
									button?.target === '_blank'
										? 'noopener noreferrer'
										: ''
								}
								className="vhs-hero__button btn btn-primary"
							>
								{button?.label}
							</a>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
export default VHSHero;
